import React, { useState, useEffect } from 'react'
import ImageUpload from './ImageUpload'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'



const EditExercise = ({ exercise, onBack, onSave, currentUser }) => {
  const { t } = useTranslation()
  const [name, setName] = useState(exercise.name || '')
  const [enName, setEnName] = useState(exercise.en_name || '')
  const [bodyPart, setBodyPart] = useState(exercise.body_part || '')
  const [imageFile, setImageFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation logic
    if (currentUser?.admin) {
      if (!name || !enName) {
        setMessage({ type: 'error', text: t('admin_requires_both_names') })
        return
      }
    } else {
      if (!name && !enName) {
        setMessage({ type: 'error', text: t('requires_at_least_one_name') })
        return
      }
    }

    setLoading(true)
    setMessage(null)

    const formData = new FormData()
    formData.append('exercise[name]', name)
    formData.append('exercise[en_name]', enName)
    formData.append('exercise[body_part]', bodyPart)
    if (imageFile) {
      formData.append('exercise[image]', imageFile)
    }

    try {
      const res = await fetch(`/api/v1/exercises/${exercise.id}`, {
        method: 'PUT', // or PATCH
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (res.ok) {
        const updatedExercise = await res.json()
        setMessage({ type: 'success', text: t('success_saved') })
        setTimeout(() => {
          onSave(updatedExercise)
        }, 1000)
      } else {
        setMessage({ type: 'error', text: t('error_occurred') })
      }
    } catch (err) {
      console.error(err)
      setMessage({ type: 'error', text: t('error_occurred') })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#111] text-white p-4 font-sans max-w-2xl mx-auto">
      <header className="flex items-center gap-4 mb-8 pt-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold">{t('edit_workout')}</h1>
      </header>

      <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
        {message && (
          <div className={`p-4 rounded-lg mb-4 ${message.type === 'success' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="edit_exercise_name" className="block text-gray-400 text-sm mb-1 font-bold">{t('exercise_name')}</label>
            <input
              id="edit_exercise_name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="edit_exercise_en_name" className="block text-gray-400 text-sm mb-1 font-bold">{t('exercise_en_name')}</label>
            <input
              id="edit_exercise_en_name"
              type="text"
              value={enName}
              onChange={(e) => setEnName(e.target.value)}
              className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="edit_body_part_select" className="block text-gray-400 text-sm mb-1 font-bold">{t('filter_by_part')}</label>
            <div className="relative">
              <select
                id="edit_body_part_select"
                value={bodyPart}
                onChange={(e) => setBodyPart(e.target.value)}
                className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
              >
                <option value="" disabled>{t('select_body_part_placeholder')}</option>
                <option value="脚">{t('body_parts.legs')}</option>
                <option value="腕">{t('body_parts.arms')}</option>
                <option value="肩">{t('body_parts.shoulders')}</option>
                <option value="背中">{t('body_parts.back')}</option>
                <option value="胸">{t('body_parts.chest')}</option>
                <option value="腹筋">{t('body_parts.abs')}</option>
                <option value="有酸素運動">{t('body_parts.cardio')}</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path></svg>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2">
              {exercise.image_url && !imageFile && (
                <div className="mb-2">
                  <p className="text-gray-400 text-sm mb-1">{t('current_image')}</p>
                  <img src={exercise.image_url} alt={name} className="h-32 rounded-lg border border-gray-700 object-cover" />
                </div>
              )}
            </div>
            <ImageUpload onImageSelect={setImageFile} label={t('machine_image_optional')} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? t('saving') : t('save')}
          </button>
        </form>
      </div>
    </div>
  )
}

export default EditExercise
